import { PencilIcon } from '@heroicons/react/20/solid';
import { useDispatch, useSelector } from 'react-redux';
import { forwardRef, useEffect, useRef, useState } from 'react';
import { TooltipV2 } from '@cimplico/components';
import { getReference, hasRestrictedSymbols, isKeyword } from '../../../../components/custom-worksheet/utils';
import { editPropertyReference, setUpdatingReferenceFieldId } from '../../store/slice';
import { selectCurrentFocusProperty, selectUpdatingReferenceField } from '../../store/selectors';
import EditInput from './EditInput';
import useIsClamped from '../../../../hooks/use-line-clamp-active';

type PropertyReferenceProps = {
    id: string;
    showReadLayout?: boolean;
};

const PropertyReference = forwardRef(function PropertyReference(
    { id, showReadLayout = true }: PropertyReferenceProps,
    ref: React.ForwardedRef<HTMLDivElement>
): JSX.Element {
    const dispatch = useDispatch();
    const currentFocusProperty = useSelector(selectCurrentFocusProperty);
    const currentUpdatingReferenceField = useSelector(selectUpdatingReferenceField);

    const isUpdatingReference = currentUpdatingReferenceField === id;
    const isFieldSidePanelOpen = currentFocusProperty === id;

    const textElementRef = useRef(null);
    const isClamped = useIsClamped(textElementRef);

    const [reference, setReference] = useState<string | undefined>(getReference(id));
    const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

    useEffect(() => {
        setReference(getReference(id));
    }, [id]);

    function handleResetValue(): void {
        setReference(getReference(id));
        setErrorMessage(undefined);
        dispatch(setUpdatingReferenceFieldId(undefined));
    }

    function handleSaveReference(): void {
        // If reference value is an empty string, revert to the previous value
        if (!reference || reference.trim() === '') {
            setReference(getReference(id));
            dispatch(setUpdatingReferenceFieldId(undefined));
            return;
        }

        const newReference = reference.trim();

        if (isKeyword(newReference)) {
            setErrorMessage(`Invalid reference: ${newReference} is a keyword`);
            return;
        }

        const restrictedSymbolsInReference = hasRestrictedSymbols(newReference);
        if (restrictedSymbolsInReference) {
            setErrorMessage(`Invalid reference: ${restrictedSymbolsInReference.join(' ')} is not allowed`);
            return;
        }

        // update reference
        if (newReference !== getReference(id)) {
            dispatch(editPropertyReference({ id, value: newReference }));
        }
        dispatch(setUpdatingReferenceFieldId(undefined));
    }

    if (!showReadLayout) {
        return (
            <EditInput
                showActionButtons={false}
                autoFocus={false}
                placeholder="Enter a field reference..."
                value={reference}
                errorMessage={errorMessage}
                onChange={(value): void => {
                    setReference(value);
                    setErrorMessage(undefined);
                }}
                onCancel={handleResetValue}
                onSave={handleSaveReference}
            />
        );
    }

    return (
        <>
            {isUpdatingReference ? (
                <EditInput
                    placeholder="Enter a field reference..."
                    value={reference}
                    errorMessage={errorMessage}
                    onChange={(value): void => {
                        setReference(value);
                        setErrorMessage(undefined);
                    }}
                    onCancel={handleResetValue}
                    onSave={handleSaveReference}
                />
            ) : (
                <div
                    ref={ref}
                    className={`w-max max-w-full ${
                        isFieldSidePanelOpen ? 'cursor-default' : 'cursor-pointer'
                    } hover:bg-gray-200 px-2 py-0.5 rounded-[5px] group border border-transparent`}
                    onClick={(): void => {
                        if (isFieldSidePanelOpen) {
                            return;
                        }
                        setReference(getReference(id));
                        dispatch(setUpdatingReferenceFieldId(id));
                    }}
                >
                    <TooltipV2 message={getReference(id)} position="right" disabled={!isClamped}>
                        <div className="flex gap-0.5 items-center w-full">
                            <p ref={textElementRef} className="p-1 text-gray-700 w-full text-sm line-clamp-1 break-words">
                                {getReference(id)}
                            </p>
                            <PencilIcon className="text-gray-500 shrink-0 w-4 h-4 hidden group-hover:block" />
                        </div>
                    </TooltipV2>
                </div>
            )}
        </>
    );
});

export default PropertyReference;
